import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Radio,
  Link,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  SwapHoriz as SwapHorizIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  transferBookingFindNearbyHotels,
  transferBookingGetRooms,
  transferBookingConfirm,
} from '../api/adminPanelApi';

function parseBookingInput(raw) {
  const s = (raw || '').trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return { booking_id: Number(s) };
  return { booking_code: s };
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '–';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(num);
}

function classifyLookupError(message) {
  const m = (message || '').toLowerCase();
  if (m.includes('not found')) return 'booking';
  if (m.includes('same hotel')) return 'hotel';
  if (m.includes('payment already transferred') || m.includes('already transferred')) return 'hard';
  if (m.includes('settlement') && m.includes('transfer')) return 'hard';
  if (m.includes('cancelled') || m.includes('canceled') || m.includes('not confirmed')) return 'block';
  return 'general';
}

const TransferBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const autoLookupDone = useRef(false);

  const [bookingInput, setBookingInput] = useState(() => {
    const fromState = location.state?.bookingId;
    return fromState != null && String(fromState).trim() !== '' ? String(fromState).trim() : '';
  });
  const [bookingFieldError, setBookingFieldError] = useState('');
  const [blockAlert, setBlockAlert] = useState('');

  const [nearbyData, setNearbyData] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [hotelListTab, setHotelListTab] = useState('available');
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [lookupData, setLookupData] = useState(null);

  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState(null);
  const [guests, setGuests] = useState('');
  const [roomCount, setRoomCount] = useState('');
  const [reason, setReason] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [successResult, setSuccessResult] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const booking = lookupData?.booking || nearbyData?.booking;
  const newHotel = lookupData?.new_hotel;
  const nearbyHotels = useMemo(
    () => [...(nearbyData?.nearby_hotels || [])].sort(
      (a, b) => Number(a.distance_km) - Number(b.distance_km)
    ),
    [nearbyData]
  );
  const soldOutHotels = useMemo(
    () => [...(nearbyData?.sold_out_hotels || [])].sort(
      (a, b) => Number(a.distance_km) - Number(b.distance_km)
    ),
    [nearbyData]
  );
  const roomTypes = useMemo(() => lookupData?.room_types || [], [lookupData]);
  const oldPaid = Number(booking?.current_totals?.final_amount_paid) || 0;

  const selectedRoom = useMemo(
    () => roomTypes.find((r) => r.room_type_id === selectedRoomTypeId) || null,
    [roomTypes, selectedRoomTypeId]
  );

  const resetAfterLookup = () => {
    setSelectedHotel(null);
    setSelectedRoomTypeId(null);
    setGuests('');
    setRoomCount('');
    setReason('');
    setSuccessResult(null);
    setConfirmOpen(false);
  };

  const handleFindNearbyHotels = async (inputOverride) => {
    setBookingFieldError('');
    setBlockAlert('');
    setNearbyData(null);
    setLookupData(null);
    resetAfterLookup();
    setHotelListTab('available');

    const raw = inputOverride != null ? String(inputOverride) : bookingInput;
    const parsed = parseBookingInput(raw);
    if (!parsed) {
      setBookingFieldError('Enter booking code or booking ID');
      return;
    }

    setLoadingNearby(true);
    try {
      const res = await transferBookingFindNearbyHotels({
        ...parsed,
        limit: 20,
        max_km: 100,
      });
      if (!res.success) {
        const kind = classifyLookupError(res.error);
        if (kind === 'booking') setBookingFieldError(res.error);
        else if (kind === 'hard' || kind === 'block' || res.error) setBlockAlert(res.error);
        else {
          setSnackbar({ open: true, message: res.error || 'Failed to find nearby hotels', severity: 'error' });
        }
        return;
      }

      const data = res.data || {};
      setNearbyData(data);
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to find nearby hotels', severity: 'error' });
    } finally {
      setLoadingNearby(false);
    }
  };

  // Prefill + auto-run nearby lookup when opened from Booking Details
  useEffect(() => {
    if (autoLookupDone.current) return;
    const id = location.state?.bookingId;
    const auto = location.state?.autoLookup;
    if (!id || !auto) return;
    autoLookupDone.current = true;
    const idStr = String(id).trim();
    setBookingInput(idStr);
    handleFindNearbyHotels(idStr);
    // clear one-shot navigation state so refresh doesn't re-fire
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFetchRooms = async (hotel = selectedHotel) => {
    setBlockAlert('');
    if (!hotel?.hotel_id) {
      setSnackbar({ open: true, message: 'Select a nearby hotel first', severity: 'warning' });
      return;
    }
    const parsed = parseBookingInput(bookingInput);
    if (!parsed) {
      setBookingFieldError('Enter booking code or booking ID');
      return;
    }

    setLoadingRooms(true);
    try {
      const res = await transferBookingGetRooms({
        ...parsed,
        new_hotel_id: hotel.hotel_id,
      });
      if (!res.success) {
        const kind = classifyLookupError(res.error);
        if (kind === 'booking') setBookingFieldError(res.error);
        else if (kind === 'hard' || kind === 'block') setBlockAlert(res.error);
        else setSnackbar({ open: true, message: res.error || 'Failed to load rooms', severity: 'error' });
        return;
      }

      const data = res.data || {};
      setLookupData(data);
      setGuests(String(data.booking?.suggested_guests_per_room ?? data.booking?.total_guests ?? ''));
      setRoomCount(String(data.booking?.suggested_room_count ?? 1));
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to load rooms', severity: 'error' });
    } finally {
      setLoadingRooms(false);
    }
  };

  const openConfirm = () => {
    if (!selectedRoom || !selectedRoom.available) {
      setSnackbar({ open: true, message: 'Select an available room type first', severity: 'warning' });
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmTransfer = async () => {
    if (!selectedRoom?.available || confirming) return;

    const parsed = parseBookingInput(bookingInput);
    if (!parsed || !selectedHotel?.hotel_id) return;

    setConfirming(true);
    try {
      const payload = {
        ...parsed,
        new_hotel_id: selectedHotel.hotel_id,
        room_type_id: selectedRoom.room_type_id,
      };
      if (guests !== '' && !Number.isNaN(Number(guests))) payload.guests = Number(guests);
      if (roomCount !== '' && !Number.isNaN(Number(roomCount))) payload.room_count = Number(roomCount);
      if (reason.trim()) payload.reason = reason.trim();

      const res = await transferBookingConfirm(payload);
      if (!res.success) {
        setConfirmOpen(false);
        const kind = classifyLookupError(res.error);
        if (kind === 'hard' || kind === 'block') setBlockAlert(res.error);
        else setSnackbar({ open: true, message: res.error || 'Transfer failed', severity: 'error' });
        return;
      }

      setConfirmOpen(false);
      setSuccessResult(res.data);
      setSnackbar({
        open: true,
        message: res.message || 'Booking transferred successfully',
        severity: 'success',
      });
      setSelectedRoomTypeId(null);
    } catch (e) {
      setConfirmOpen(false);
      setSnackbar({ open: true, message: e.message || 'Transfer failed', severity: 'error' });
    } finally {
      setConfirming(false);
    }
  };

  const handleStartAnother = () => {
    setBookingInput('');
    setNearbyData(null);
    setLookupData(null);
    resetAfterLookup();
    setBlockAlert('');
    setBookingFieldError('');
  };

  const deltaColor = (delta) => {
    if (delta == null || Number.isNaN(Number(delta))) return 'text.secondary';
    const n = Number(delta);
    if (n > 0) return '#c62828';
    if (n < 0) return '#2e7d32';
    return 'text.secondary';
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <IconButton
          onClick={() => navigate('/')}
          size="small"
          sx={{
            color: '#FF7700',
            bgcolor: '#fff',
            border: '1px solid #e4e8ee',
            boxShadow: '0 2px 8px rgba(46, 64, 87, 0.08)',
            '&:hover': { bgcolor: '#fff5eb' },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#243447', letterSpacing: '-0.02em' }}>
            Transfer booking to another hotel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reprice a confirmed booking and move it safely to another nearby property.
          </Typography>
        </Box>
      </Box>

      {blockAlert && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBlockAlert('')}>
          {blockAlert}
        </Alert>
      )}

      {successResult && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ mb: 2, border: '1px solid #b7dfc5', boxShadow: '0 3px 12px rgba(46, 125, 50, 0.08)' }}
          action={
            <Button color="inherit" size="small" onClick={handleStartAnother}>
              New transfer
            </Button>
          }
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {successResult.booking_code || booking?.booking_code} moved to{' '}
            {successResult.to_hotel_name || newHotel?.hotel_name}
          </Typography>
          <Typography variant="body2">
            New paid:{' '}
            {formatMoney(
              successResult.money?.final_amount_paid ??
                successResult.booking?.final_amount_paid
            )}
            {successResult.amount_delta_vs_old_paid != null && (
              <>
                {' '}
                · Delta vs old:{' '}
                <Box component="span" sx={{ color: deltaColor(successResult.amount_delta_vs_old_paid), fontWeight: 600 }}>
                  {formatMoney(successResult.amount_delta_vs_old_paid)}
                </Box>
              </>
            )}
          </Typography>
          {(successResult.booking_code || booking?.booking_code) && (
            <Link
              component={RouterLink}
              to="/booking-details"
              underline="hover"
              sx={{ fontSize: '0.875rem', mt: 0.5, display: 'inline-block' }}
            >
              Open booking details
            </Link>
          )}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 2.5, border: '1px solid #e4e8ee', borderLeft: '4px solid #FF7700', boxShadow: '0 4px 16px rgba(46, 64, 87, 0.06)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="overline" sx={{ color: '#FF7700', fontWeight: 800, letterSpacing: '0.08em', lineHeight: 1 }}>
              Step 1
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.5, color: '#2E4057' }}>
              Find nearby hotels
            </Typography>
          </Box>
          <Chip label="Same-state search" size="small" sx={{ bgcolor: '#fff4e8', color: '#a84d00', fontWeight: 600 }} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter a booking code or booking ID to find nearby hotels in the same state.
        </Typography>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={7} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Booking"
              placeholder="TT451252261 or 2261"
              value={bookingInput}
              onChange={(e) => {
                setBookingInput(e.target.value);
                setBookingFieldError('');
              }}
              error={!!bookingFieldError}
              helperText={bookingFieldError || 'Booking code or numeric booking ID'}
              disabled={loadingNearby || loadingRooms || confirming}
            />
          </Grid>
          <Grid item xs={12} sm={5} md={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={loadingNearby ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={handleFindNearbyHotels}
              disabled={loadingNearby || loadingRooms || confirming}
              sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' }, height: 42, boxShadow: '0 4px 10px rgba(255, 119, 0, 0.25)' }}
            >
              Find nearby hotels
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {nearbyData && booking && (
        <>
          <Grid container spacing={2} sx={{ mb: 2, display: { xs: 'block', lg: 'grid' }, gridTemplateColumns: { lg: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' } }}>
            <Grid item xs={12} md={6} sx={{ minWidth: 0, mb: { xs: 2, lg: 0 } }}>
              <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2.5, height: '100%', border: '1px solid #e4e8ee', boxShadow: '0 4px 16px rgba(46, 64, 87, 0.05)' }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                  Current booking
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#2E4057' }}>
                  {booking.booking_code}
                  {booking.booking_id != null && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      #{booking.booking_id}
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {booking.guest_name || '–'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {booking.check_in_date} → {booking.check_out_date}
                  {booking.total_guests != null && ` · ${booking.total_guests} guest(s)`}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Hotel: <strong>{booking.current_hotel_name}</strong>
                  {booking.current_hotel_id != null && ` (${booking.current_hotel_id})`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  State: {booking.state_name || '–'}
                </Typography>
                {(booking.address || booking.current_hotel_address) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {booking.address || booking.current_hotel_address}
                  </Typography>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Guest actually paid</Typography>
                    <Typography fontWeight={600}>{formatMoney(booking.current_totals?.final_amount_paid)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Discounted room price</Typography>
                    <Typography fontWeight={600}>{formatMoney(booking.current_totals?.discounted_amount)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Room GST</Typography>
                    <Typography fontWeight={600}>{formatMoney(booking.current_totals?.gst_amount)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Base room price</Typography>
                    <Typography fontWeight={600}>{formatMoney(booking.current_totals?.total_amount)}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
              <Paper sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2.5, height: '100%', border: '1px solid #e4e8ee', boxShadow: '0 4px 16px rgba(46, 64, 87, 0.05)' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2E4057' }}>
                  Nearby hotels
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Same-state hotels sorted by distance.
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={hotelListTab}
                  onChange={(_, value) => value && setHotelListTab(value)}
                  sx={{
                    mb: 1.5,
                    '& .MuiToggleButton-root': {
                      px: 1.5,
                      py: 0.65,
                      borderColor: '#e1e6ec',
                      color: '#607080',
                      fontWeight: 700,
                      textTransform: 'none',
                    },
                    '& .Mui-selected': {
                      bgcolor: '#fff1e5 !important',
                      color: '#c45d00 !important',
                      borderColor: '#ffb36b !important',
                    },
                  }}
                >
                  <ToggleButton value="available">
                    Available ({nearbyHotels.length})
                  </ToggleButton>
                  <ToggleButton value="sold_out">
                    Sold out ({soldOutHotels.length})
                  </ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ maxHeight: 330, overflowY: 'auto' }}>
                  {(hotelListTab === 'available' ? nearbyHotels : soldOutHotels).length === 0 ? (
                    <Alert severity="info">
                      {hotelListTab === 'available'
                        ? 'No available hotels nearby'
                        : 'No sold-out hotels in range'}
                    </Alert>
                  ) : (
                    (hotelListTab === 'available' ? nearbyHotels : soldOutHotels).map((hotel) => {
                      const isSoldOut = hotelListTab === 'sold_out';
                      const selected = !isSoldOut && selectedHotel?.hotel_id === hotel.hotel_id;
                      return (
                        <Paper
                          key={hotel.hotel_id}
                          variant="outlined"
                          onClick={() => {
                            if (isSoldOut || loadingRooms) {
                              if (isSoldOut) {
                                setSnackbar({
                                  open: true,
                                  message: 'No rooms available for these dates',
                                  severity: 'info',
                                });
                              }
                              return;
                            }
                            setLookupData(null);
                            resetAfterLookup();
                            setSelectedHotel(hotel);
                            handleFetchRooms(hotel);
                          }}
                          sx={{
                            p: 1.25,
                            mb: 1,
                            cursor: isSoldOut ? 'not-allowed' : loadingRooms ? 'wait' : 'pointer',
                            opacity: isSoldOut ? 0.65 : 1,
                            borderColor: selected ? '#FF7700' : 'divider',
                            borderWidth: selected ? 2 : 1,
                            bgcolor: selected ? 'rgba(255, 119, 0, 0.07)' : 'background.paper',
                            transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                            '&:hover': !isSoldOut && !loadingRooms ? {
                              borderColor: '#ff9a45',
                              boxShadow: '0 4px 12px rgba(46, 64, 87, 0.08)',
                              transform: 'translateY(-1px)',
                            } : undefined,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700} sx={{ color: '#2E4057' }}>{hotel.hotel_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID {hotel.hotel_id} · {hotel.address || 'Address unavailable'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                              <Chip
                                label={hotel.distance_label || `${hotel.distance_km ?? '–'} km`}
                                color={selected ? 'primary' : 'default'}
                                size="small"
                                sx={{ height: 25, fontWeight: 800, flexShrink: 0, minWidth: 64 }}
                              />
                              {isSoldOut && (
                                <Chip
                                  label="Sold out"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{ height: 21, fontSize: 11 }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })
                  )}
                </Box>
                {hotelListTab === 'available' && selectedHotel && (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleFetchRooms(selectedHotel)}
                    disabled={loadingRooms || confirming}
                    startIcon={loadingRooms ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                    sx={{ mt: 1, bgcolor: '#2E4057', '&:hover': { bgcolor: '#1A252F' } }}
                  >
                    {loadingRooms ? 'Fetching rooms…' : `Fetch rooms at ${selectedHotel.hotel_name}`}
                  </Button>
                )}
              </Paper>
            </Grid>
          </Grid>

          {lookupData && (
          <>
          <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 2.5, border: '1px solid #c9d7e5', borderLeft: '4px solid #2E4057', boxShadow: '0 4px 16px rgba(46, 64, 87, 0.05)' }}>
            <Typography variant="overline" sx={{ color: '#607080', fontWeight: 800, letterSpacing: '0.08em', lineHeight: 1 }}>
              Step 2
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.5, color: '#2E4057' }}>
              Reprice at selected hotel
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{ mt: 1, color: '#2E4057' }}>
              {newHotel?.hotel_name || selectedHotel?.hotel_name || '–'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {newHotel?.hotel_id ?? selectedHotel?.hotel_id ?? '–'}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Guests"
                type="number"
                value={guests}
                onChange={(e) => setGuests(e.target.value)}
                inputProps={{ min: 1 }}
                sx={{ width: 120 }}
                helperText="Optional override"
              />
              <TextField
                size="small"
                label="Room count"
                type="number"
                value={roomCount}
                onChange={(e) => setRoomCount(e.target.value)}
                inputProps={{ min: 1 }}
                sx={{ width: 120 }}
                helperText="Optional override"
              />
            </Box>
          </Paper>

          <Paper sx={{ borderRadius: 2.5, overflow: 'hidden', mb: 2, border: '1px solid #e4e8ee', boxShadow: '0 4px 16px rgba(46, 64, 87, 0.05)' }}>
            <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, bgcolor: '#fbfcfe', borderBottom: '1px solid #e4e8ee' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2E4057' }}>
                Select a room type
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose an available room to reprice and move the booking + settlement.
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f7fa' }}>Room type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Occ.</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Guests</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Rooms</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Availability</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Guest actually paid</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Δ vs old</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">GST</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Discounted price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Base price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roomTypes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                        No room types returned for this hotel.
                      </TableCell>
                    </TableRow>
                  )}
                  {roomTypes.map((rt) => {
                    const paid = Number(rt.totals?.final_amount_paid) || 0;
                    const delta = paid - oldPaid;
                    const available = !!rt.available;
                    const selected = selectedRoomTypeId === rt.room_type_id;
                    return (
                      <TableRow
                        key={rt.room_type_id}
                        hover={available}
                        selected={selected}
                        onClick={() => available && setSelectedRoomTypeId(rt.room_type_id)}
                        sx={{
                          cursor: available ? 'pointer' : 'not-allowed',
                          opacity: available ? 1 : 0.55,
                          bgcolor: selected ? 'rgba(255, 119, 0, 0.08)' : undefined,
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Radio
                            size="small"
                            checked={selected}
                            disabled={!available}
                            onChange={() => available && setSelectedRoomTypeId(rt.room_type_id)}
                            sx={{ color: '#FF7700', '&.Mui-checked': { color: '#FF7700' } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{rt.room_type_name}</Typography>
                          <Typography variant="caption" color="text.secondary">ID {rt.room_type_id}</Typography>
                        </TableCell>
                        <TableCell align="center">{rt.occupancy ?? '–'}</TableCell>
                        <TableCell align="center">{rt.guests ?? '–'}</TableCell>
                        <TableCell align="center">{rt.room_count ?? '–'}</TableCell>
                        <TableCell>
                          {available ? (
                            <Chip label="Available" size="small" color="success" sx={{ height: 22, fontSize: 11 }} />
                          ) : (
                            <Chip
                              label={rt.error || 'Unavailable'}
                              size="small"
                              color="default"
                              sx={{ height: 22, fontSize: 11, maxWidth: 180 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">{formatMoney(rt.totals?.final_amount_paid)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} sx={{ color: deltaColor(delta) }}>
                            {delta > 0 ? '+' : ''}{formatMoney(delta)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatMoney(rt.totals?.gst_amount)}</TableCell>
                        <TableCell align="right">{formatMoney(rt.totals?.discounted_amount)}</TableCell>
                        <TableCell align="right">{formatMoney(rt.totals?.total_amount)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', borderTop: '1px solid #eee' }}>
              <TextField
                size="small"
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Guest requested different property"
                sx={{ flex: 1, minWidth: 240 }}
                disabled={confirming}
              />
              <Button
                variant="contained"
                startIcon={<SwapHorizIcon />}
                onClick={openConfirm}
                disabled={!selectedRoom?.available || confirming || !!successResult}
                sx={{ bgcolor: '#2E4057', '&:hover': { bgcolor: '#1A252F' } }}
              >
                Confirm transfer
              </Button>
            </Box>
          </Paper>
          </>
          )}
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => !confirming && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm booking transfer</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Razorpay payment capture stays as-is; booking + settlement amounts move to the new hotel.
            Blocked if settlement already transferred.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>From:</strong> {booking?.current_hotel_name} ({booking?.current_hotel_id})
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>To:</strong> {newHotel?.hotel_name || selectedHotel?.hotel_name} (
            {newHotel?.hotel_id ?? selectedHotel?.hotel_id})
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Room:</strong> {selectedRoom?.room_type_name} · guests {guests || selectedRoom?.guests} · rooms{' '}
            {roomCount || selectedRoom?.room_count}
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Old guest paid</Typography>
              <Typography fontWeight={600}>{formatMoney(oldPaid)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">New guest paid</Typography>
              <Typography fontWeight={600}>{formatMoney(selectedRoom?.totals?.final_amount_paid)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">New GST</Typography>
              <Typography fontWeight={600}>{formatMoney(selectedRoom?.totals?.gst_amount)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Delta vs old</Typography>
              <Typography fontWeight={600} sx={{ color: deltaColor((Number(selectedRoom?.totals?.final_amount_paid) || 0) - oldPaid) }}>
                {formatMoney((Number(selectedRoom?.totals?.final_amount_paid) || 0) - oldPaid)}
              </Typography>
            </Grid>
          </Grid>
          {reason.trim() && (
            <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
              Reason: {reason.trim()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={confirming}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmTransfer}
            disabled={confirming}
            startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
            sx={{ bgcolor: '#FF7700', '&:hover': { bgcolor: '#e65100' } }}
          >
            {confirming ? 'Transferring…' : 'Confirm transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </Box>
  );
};

export default TransferBooking;
